// ============================================================
// /square/projects/:id/hosting — Hosting + DNS surface (V3).
//
// Consumes the HostingV3 presentational primitives (Section,
// ActionButton, StepCard, EmptyTile). All data comes from real
// API routes; no static fake rows. The DNS walkthrough is kept
// as clearly labeled instructional content.
//
// Live provider snapshot comes from GET /api/hosting/vercel/project-status,
// which proxies the Vercel API server-side. The response has three
// honest shapes: ok | unavailable | provider_error.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { InputField } from '@square/ui'
import { SHELL_TOKENS, timeAgo } from '../../shell/styles'
import EmptyState from '../../components/EmptyState'
import ProjectSurface from './ProjectSurface'
import { api, ApiError, isAuthError } from '../../api/client'
import type {
  HostingDeploymentApiRow,
  HostingDomainApiRow,
  VercelDeploymentRow,
  VercelDomainRow,
} from '../../api/client'
import { useProject } from '../../hooks/useProjects'
import { V3 } from './HostingV3/tokens'
import {
  V3Styles,
  Section,
  ActionButton,
  StepCard,
  EmptyTile,
} from './HostingV3/components'

type VercelStatus =
  | {
      ok: true
      unavailable: false
      project_name: string
      latest_production: VercelDeploymentRow | null
      latest_preview: VercelDeploymentRow | null
      domains: VercelDomainRow[]
      checked_at: string
    }
  | {
      ok: false
      unavailable: true
      reason: 'provider_token_missing'
      checked_at: string
    }
  | {
      ok: false
      unavailable: false
      reason: 'provider_error'
      error: string
      checked_at: string
    }

export default function ProjectHosting() {
  const { id } = useParams<{ id: string }>()
  const { project } = useProject(id)

  // --- Square Code database state (sq_deployments + sq_domains) ---
  const [deployments, setDeployments] = useState<HostingDeploymentApiRow[]>([])
  const [domains, setDomains] = useState<HostingDomainApiRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)

  // --- Live Vercel snapshot (server-side proxy) ---
  const [vercelStatus, setVercelStatus] = useState<VercelStatus | null>(null)
  const [vercelLoading, setVercelLoading] = useState(false)

  // --- Operator action state ---
  const [environment, setEnvironment] = useState<'production' | 'preview' | 'local'>('preview')
  const [provider, setProvider] = useState('vercel')
  const [hostname, setHostname] = useState('')
  const [domainEnvironment, setDomainEnvironment] = useState<'production' | 'preview' | 'local'>('production')
  const [submittingDeploy, setSubmittingDeploy] = useState(false)
  const [submittingDomain, setSubmittingDomain] = useState(false)
  const [verifyingId, setVerifyingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  function formatError(err: unknown): string {
    if (isAuthError(err)) return 'Sign in to manage hosting.'
    if (err instanceof ApiError) {
      return err.message && err.message !== err.code ? `${err.code} · ${err.message}` : err.code
    }
    if (err instanceof Error) return err.message || 'hosting_action_failed'
    return 'hosting_action_failed'
  }

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setAuthRequired(false)
    try {
      const [deploymentRes, domainRes] = await Promise.all([
        api.hosting.deployments({ project_id: id, limit: 30 }),
        api.hosting.domains(id),
      ])
      setDeployments(deploymentRes.deployments || [])
      setDomains(domainRes.domains || [])
    } catch (err) {
      if (isAuthError(err)) {
        setAuthRequired(true)
      } else if (err instanceof ApiError) {
        setError(err.message && err.message !== err.code ? `${err.code} · ${err.message}` : err.code)
      } else {
        setError((err as Error).message || 'hosting_load_failed')
      }
      setDeployments([])
      setDomains([])
    } finally {
      setLoading(false)
    }
  }, [id])

  const loadVercelStatus = useCallback(async () => {
    if (!project?.slug) return
    setVercelLoading(true)
    try {
      const result = await api.hosting.vercelProjectStatus(project.slug)
      setVercelStatus(result as VercelStatus)
    } catch (err) {
      // Treat fetch failure as provider_error (honest state)
      setVercelStatus({
        ok: false,
        unavailable: false,
        reason: 'provider_error',
        error: err instanceof ApiError ? err.message : String(err),
        checked_at: new Date().toISOString(),
      })
    } finally {
      setVercelLoading(false)
    }
  }, [project?.slug])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadVercelStatus()
  }, [loadVercelStatus])

  async function triggerDeployment() {
    if (!id || submittingDeploy) return
    setSubmittingDeploy(true)
    setActionMessage(null)
    try {
      await api.hosting.triggerDeployment({ project_id: id, environment, provider })
      setActionMessage(`Deployment intent recorded · ${environment} · ${provider}`)
      await load()
    } catch (err) {
      setActionMessage(formatError(err))
    } finally {
      setSubmittingDeploy(false)
    }
  }

  async function addDomain() {
    if (!id || !hostname.trim() || submittingDomain) return
    setSubmittingDomain(true)
    setActionMessage(null)
    try {
      await api.hosting.addDomain({
        project_id: id,
        hostname: hostname.trim(),
        environment: domainEnvironment,
      })
      setActionMessage(`Domain added · ${hostname.trim()}`)
      setHostname('')
      await load()
    } catch (err) {
      setActionMessage(formatError(err))
    } finally {
      setSubmittingDomain(false)
    }
  }

  async function verifyDomain(domainId: string) {
    if (verifyingId) return
    setVerifyingId(domainId)
    setActionMessage(null)
    try {
      const result = await api.hosting.verifyDomain(domainId)
      setActionMessage(result.verification?.message || 'Verification submitted')
      await load()
    } catch (err) {
      setActionMessage(formatError(err))
    } finally {
      setVerifyingId(null)
    }
  }

  async function deleteDomain(domainId: string, domainHostname: string) {
    if (deletingId) return
    if (!window.confirm(`Delete domain ${domainHostname}?`)) return
    setDeletingId(domainId)
    setActionMessage(null)
    try {
      await api.hosting.deleteDomain(domainId)
      setActionMessage(`Domain deleted · ${domainHostname}`)
      await load()
    } catch (err) {
      setActionMessage(formatError(err))
    } finally {
      setDeletingId(null)
    }
  }

  const activeDomainCount = useMemo(
    () => domains.filter((d) => d.status === 'active').length,
    [domains],
  )

  return (
    <ProjectSurface
      kicker="PROJECT · HOSTING"
      title="Hosting & DNS"
      description="Square Code orchestrates real provider infrastructure. Deployments and domains are stored in Square Code; live provider snapshots come from the Vercel API."
    >
      <V3Styles />

      {/* Action feedback */}
      {actionMessage && (
        <div
          style={{
            marginBottom: 20,
            padding: '12px 16px',
            borderRadius: 12,
            border: `1px solid ${V3.pur}20`,
            background: V3.pur + '06',
            color: V3.w2,
            fontSize: 12,
            fontFamily: V3.mono,
          }}
        >
          {actionMessage}
        </div>
      )}

      {/* ============================================================
         PROVIDER STATUS — live Vercel snapshot
         ============================================================ */}
      <Section label="PROVIDER STATUS" color={V3.blue}>
        {vercelLoading && !vercelStatus ? (
          <EmptyTile title="Loading provider status" body="Calling Vercel API…" kind="empty" />
        ) : !vercelStatus ? (
          <EmptyTile
            title="Project slug required"
            body="Vercel status is queried by project slug. Set a slug on this project to load live provider state."
            kind="empty"
          />
        ) : vercelStatus.unavailable ? (
          <EmptyTile
            title="Vercel not configured"
            body="VERCEL_ACCESS_TOKEN is not set on the server. Deploy intent is recorded in Square Code, but live provider data is unavailable until the token is configured."
            kind="unavailable"
          />
        ) : !vercelStatus.ok ? (
          <EmptyTile
            title="Provider error"
            body={vercelStatus.error}
            kind="unavailable"
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 10,
            }}
          >
            <VercelDeployCard
              label="Latest production"
              deploy={vercelStatus.latest_production}
              color={V3.grn}
            />
            <VercelDeployCard
              label="Latest preview"
              deploy={vercelStatus.latest_preview}
              color={V3.blue}
            />
          </div>
        )}
      </Section>

      {/* ============================================================
         DEPLOY CONTROLS — real operator actions
         ============================================================ */}
      <Section label="DEPLOY" color={V3.grn}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <InputField
            as="select"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as 'production' | 'preview' | 'local')}
          >
            <option value="production">production</option>
            <option value="preview">preview</option>
            <option value="local">local</option>
          </InputField>
          <InputField value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="provider" />
        </div>
        <ActionButton
          icon="▶"
          label={submittingDeploy ? 'Queuing…' : 'Queue Deployment'}
          sub="Records intent in Square Code. Provider dispatch deferred."
          color={V3.grn}
          disabled={!id || submittingDeploy}
          loading={submittingDeploy}
          onClick={triggerDeployment}
          apiPath="/api/hosting/deployments/trigger"
          method="POST"
        />
      </Section>

      {/* ============================================================
         ADD DOMAIN — real operator action
         ============================================================ */}
      <Section label="ADD DOMAIN" color={V3.cyan}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <InputField
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            placeholder="example.com"
          />
          <InputField
            as="select"
            value={domainEnvironment}
            onChange={(e) =>
              setDomainEnvironment(e.target.value as 'production' | 'preview' | 'local')
            }
          >
            <option value="production">production</option>
            <option value="preview">preview</option>
            <option value="local">local</option>
          </InputField>
        </div>
        <ActionButton
          icon="＋"
          label={submittingDomain ? 'Adding…' : 'Add Domain'}
          sub="Stores domain in Square Code. Run verify next."
          color={V3.cyan}
          disabled={!id || !hostname.trim() || submittingDomain}
          loading={submittingDomain}
          onClick={addDomain}
          apiPath="/api/hosting/domains/add"
          method="POST"
        />
      </Section>

      {/* ============================================================
         ACTIVE DOMAINS — real from sq_domains
         ============================================================ */}
      <Section label="ACTIVE DOMAINS" color={V3.grn}>
        {loading ? (
          <EmptyTile title="Loading domains" body="Reading sq_domains." kind="empty" />
        ) : authRequired ? (
          <EmptyTile
            title="Sign in required"
            body="Hosting data is owner-scoped. Sign in from /auth/login to continue."
            kind="auth"
          />
        ) : error ? (
          <EmptyTile title="Hosting unavailable" body={error} kind="unavailable" />
        ) : domains.length === 0 ? (
          <EmptyTile
            title="No domains yet"
            body="Add a domain above to start the DNS workflow."
            kind="empty"
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {domains.map((d) => (
              <DomainRow
                key={d.id}
                domain={d}
                verifying={verifyingId === d.id}
                deleting={deletingId === d.id}
                onVerify={() => void verifyDomain(d.id)}
                onDelete={() => void deleteDomain(d.id, d.hostname)}
              />
            ))}
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: V3.w3,
            marginTop: 10,
            fontFamily: V3.mono,
          }}
        >
          {activeDomainCount} of {domains.length} active
        </div>
      </Section>

      {/* ============================================================
         DEPLOYMENT HISTORY — real from sq_deployments
         ============================================================ */}
      <Section label="DEPLOYMENT HISTORY" color={V3.pur}>
        {loading ? (
          <EmptyTile title="Loading deployments" body="Reading sq_deployments." kind="empty" />
        ) : authRequired ? (
          <EmptyTile
            title="Sign in required"
            body="Hosting data is owner-scoped."
            kind="auth"
          />
        ) : deployments.length === 0 ? (
          <EmptyTile
            title="No deployments yet"
            body="Queue a deployment above to create the first row."
            kind="empty"
          />
        ) : (
          <div
            style={{
              borderRadius: 14,
              background: V3.pur + '02',
              border: '1px solid ' + V3.pur + '06',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 90px 1fr 100px 80px',
                gap: 8,
                padding: '10px 18px',
                borderBottom: '1px solid ' + V3.pur + '06',
              }}
            >
              {['ENVIRONMENT', 'PROVIDER', 'URL', 'STATUS', 'AGE'].map((h) => (
                <span
                  key={h}
                  style={{
                    fontSize: 9,
                    fontFamily: V3.mono,
                    fontWeight: 700,
                    color: V3.pur,
                    letterSpacing: 2,
                  }}
                >
                  {h}
                </span>
              ))}
            </div>
            {deployments.slice(0, 10).map((d) => (
              <DeploymentHistoryRow key={d.id} deployment={d} />
            ))}
          </div>
        )}
      </Section>

      {/* ============================================================
         DNS WALKTHROUGH — INSTRUCTIONAL CONTENT, clearly labeled
         ============================================================ */}
      <Section label="DNS WALKTHROUGH — OPERATOR GUIDANCE" color={V3.gld}>
        <div
          style={{
            fontSize: 11,
            color: V3.w3,
            marginBottom: 12,
            fontFamily: V3.mono,
            letterSpacing: '0.04em',
          }}
        >
          The steps below are general guidance for pointing a domain at Vercel. Specific
          DNS records must be configured at your DNS provider (Cloudflare, Namecheap, etc.)
          — Square Code does not manage DNS directly.
        </div>
        <StepCard
          step="1"
          title="Add domain to Vercel project"
          desc="Use the Add Domain action above, or add it directly in the Vercel dashboard."
          status="ready"
        >
          <div style={{ fontSize: 11, color: V3.w3, fontFamily: V3.mono }}>
            Square Code stores the domain row. Vercel attaches it when the token is
            configured and the project slug matches.
          </div>
        </StepCard>

        <StepCard
          step="2"
          title="Configure DNS records at your provider"
          desc="Example records for pointing a root domain and www subdomain at Vercel."
          status="ready"
        >
          <div
            style={{
              fontSize: 10,
              fontFamily: V3.mono,
              color: V3.w3,
              marginBottom: 8,
              fontStyle: 'italic',
            }}
          >
            Example — not live provider data. Configure these at your DNS provider.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <DnsExampleRow type="A" name="@" value="76.76.21.21" note="Root domain → Vercel" />
            <DnsExampleRow type="CNAME" name="www" value="cname.vercel-dns.com" note="www → Vercel" />
          </div>
        </StepCard>

        <StepCard
          step="3"
          title="Verify domain ownership"
          desc="Use the Check Status action on each domain row above to trigger verification."
          status="ready"
        >
          <div style={{ fontSize: 11, color: V3.w3, fontFamily: V3.mono }}>
            Square Code updates the domain status in sq_domains. Provider-side DNS
            validation is deferred in this pass.
          </div>
        </StepCard>

        <StepCard
          step="4"
          title="SSL certificate"
          desc="Auto-provisioned by Vercel after successful DNS verification."
          status="ready"
        >
          <div style={{ fontSize: 11, color: V3.w3, fontFamily: V3.mono }}>
            Vercel auto-provisions SSL via Let's Encrypt after domain verification.
            No action required from Square Code.
          </div>
        </StepCard>
      </Section>
    </ProjectSurface>
  )
}

// ----------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------

function VercelDeployCard({
  label,
  deploy,
  color,
}: {
  label: string
  deploy: VercelDeploymentRow | null
  color: string
}) {
  if (!deploy) {
    return (
      <div
        style={{
          padding: '16px 18px',
          borderRadius: 12,
          background: V3.pur + '02',
          border: '1px solid ' + V3.pur + '08',
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontFamily: V3.mono,
            fontWeight: 700,
            color: V3.w3,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          {label.toUpperCase()}
        </div>
        <div style={{ fontSize: 13, color: V3.w2 }}>No deployment</div>
      </div>
    )
  }

  const ready = deploy.state === 'READY' || deploy.readyState === 'READY'
  const stateColor = ready ? V3.grn : V3.gld

  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: 12,
        background: color + '06',
        border: '1px solid ' + color + '18',
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontFamily: V3.mono,
          fontWeight: 700,
          color,
          letterSpacing: 2,
          marginBottom: 8,
        }}
      >
        {label.toUpperCase()}
      </div>
      <div
        style={{
          fontSize: 13,
          fontFamily: V3.mono,
          color: V3.w,
          wordBreak: 'break-all',
          marginBottom: 8,
        }}
      >
        {deploy.url}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span
          style={{
            fontSize: 10,
            fontFamily: V3.mono,
            fontWeight: 700,
            color: stateColor,
            letterSpacing: 1,
          }}
        >
          {deploy.state}
        </span>
        <span style={{ fontSize: 10, color: V3.w3, fontFamily: V3.mono }}>
          {timeAgo(new Date(deploy.created).toISOString())}
        </span>
      </div>
    </div>
  )
}

function DomainRow({
  domain,
  verifying,
  deleting,
  onVerify,
  onDelete,
}: {
  domain: HostingDomainApiRow
  verifying: boolean
  deleting: boolean
  onVerify: () => void
  onDelete: () => void
}) {
  const sc =
    domain.status === 'active'
      ? V3.grn
      : domain.status === 'verifying'
      ? V3.gld
      : domain.status === 'error'
      ? V3.red
      : V3.w3

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 18px',
        borderRadius: 12,
        background: V3.pur + '02',
        border: '1px solid ' + V3.pur + '06',
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: sc,
          boxShadow: '0 0 4px ' + sc + '50',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontFamily: V3.mono,
            color: V3.w,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {domain.hostname}
        </div>
        <div
          style={{
            fontSize: 10,
            color: V3.w3,
            marginTop: 2,
            fontFamily: V3.mono,
          }}
        >
          {domain.environment} · {domain.status}
        </div>
      </div>
      <button
        type="button"
        onClick={onVerify}
        disabled={verifying}
        style={rowActionStyle(V3.cyan, verifying)}
      >
        {verifying ? 'CHECKING' : 'CHECK'}
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        style={rowActionStyle(V3.red, deleting)}
      >
        {deleting ? 'DELETING' : 'DELETE'}
      </button>
    </div>
  )
}

function DeploymentHistoryRow({ deployment }: { deployment: HostingDeploymentApiRow }) {
  const color =
    deployment.status === 'success' || deployment.status === 'ready'
      ? V3.grn
      : deployment.status === 'building' || deployment.status === 'pending'
      ? V3.gld
      : deployment.status === 'error' || deployment.status === 'failed'
      ? V3.red
      : V3.w3

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 90px 1fr 100px 80px',
        gap: 8,
        padding: '14px 18px',
        borderBottom: '1px solid ' + V3.pur + '03',
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 12, color: V3.w, fontWeight: 500, fontFamily: V3.mono }}>
        {deployment.environment}
      </span>
      <span
        style={{
          fontSize: 10,
          fontFamily: V3.mono,
          fontWeight: 600,
          color: V3.blue,
          letterSpacing: 1,
        }}
      >
        {deployment.provider}
      </span>
      <span
        style={{
          fontSize: 11,
          fontFamily: V3.mono,
          color: V3.w3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {deployment.deploy_url || '—'}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: color,
          }}
        />
        <span
          style={{
            fontSize: 10,
            fontFamily: V3.mono,
            fontWeight: 700,
            color,
            letterSpacing: 1,
          }}
        >
          {deployment.status.toUpperCase()}
        </span>
      </div>
      <span style={{ fontSize: 11, color: V3.w3, fontFamily: V3.mono }}>
        {timeAgo(deployment.created_at)}
      </span>
    </div>
  )
}

function DnsExampleRow({
  type,
  name,
  value,
  note,
}: {
  type: string
  name: string
  value: string
  note: string
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '60px 80px 1fr 1fr',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 8,
        background: V3.pur + '03',
        border: '1px solid ' + V3.pur + '06',
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 12, fontFamily: V3.mono, fontWeight: 700, color: V3.blue }}>
        {type}
      </span>
      <span style={{ fontSize: 12, fontFamily: V3.mono, color: V3.w }}>{name}</span>
      <span style={{ fontSize: 12, fontFamily: V3.mono, color: V3.w2, wordBreak: 'break-all' }}>
        {value}
      </span>
      <span style={{ fontSize: 10, color: V3.w3, fontFamily: V3.mono }}>{note}</span>
    </div>
  )
}

function rowActionStyle(accent: string, disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 14px',
    borderRadius: 8,
    border: `1px solid ${accent}30`,
    background: `${accent}0c`,
    color: accent,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.15em',
    fontFamily: V3.mono,
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.25s',
  }
}
